import {
  CreativeAspect,
  CreativeFinish,
  CreativeJob,
  CreativeJobStatus,
  CreativeOutputRequest,
  CreativeOutputType,
} from "api/creativeStudio";

export const DESCRIPTION_MIN = 10;
export const DESCRIPTION_MAX = 4000;
export const MAX_KLYDOS = 12;
export const MAX_REFERENCE_IMAGES = 6;
export const MAX_REFERENCE_IMAGE_BYTES = 15 * 1024 * 1024;
export const REFERENCE_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

export const IMAGE_COUNT = { min: 1, max: 4, default: 2 };
export const VIDEO_DURATION = { min: 5, max: 30, default: 15 };
export const KLYDO_DURATION = { min: 4, max: 20, default: 10 };

export const OUTPUT_TYPE_OPTIONS: {
  value: CreativeOutputType;
  label: string;
}[] = [
  { value: "images", label: "Image(s)" },
  { value: "video", label: "Video (ad / promo)" },
  { value: "klydo", label: "Klydo animation (plays on the clock)" },
];

export const ASPECT_OPTIONS: { value: CreativeAspect; label: string }[] = [
  { value: "1:1", label: "Square 1:1 (feed)" },
  { value: "4:5", label: "Portrait 4:5 (Instagram)" },
  { value: "9:16", label: "Story 9:16 (Reels/TikTok)" },
  { value: "16:9", label: "Wide 16:9 (YouTube/web)" },
];

export const FINISH_OPTIONS: { value: CreativeFinish; label: string }[] = [
  { value: "oak", label: "Dawn Oak" },
  { value: "walnut", label: "Twilight Walnut" },
  { value: "ebony", label: "Midnight Ebony" },
  { value: "any", label: "Any" },
];

export const DESCRIPTION_PLACEHOLDERS: Record<CreativeOutputType, string> = {
  images:
    "A cozy autumn living-room shot of the walnut Klydoclock showing the Spooky Nights collection, with the headline 'Time to get spooky'",
  video:
    "A cozy autumn living-room shot of the walnut Klydoclock showing the Spooky Nights collection, with the headline 'Time to get spooky'",
  klydo: "A calm koi pond seen from above, slow ripples, warm sunset colors",
};

/** What one job of each type usually costs in Claude usage. */
export const COST_ESTIMATES: Record<CreativeOutputType, string> = {
  images: "$1–3",
  video: "$2–6",
  klydo: "$2–6",
};

export const OUTPUT_TYPE_NOUNS: Record<CreativeOutputType, string> = {
  images: "image set",
  video: "video",
  klydo: "klydo animation",
};

export const STATUS_COLORS: Record<
  CreativeJobStatus,
  "default" | "info" | "secondary" | "primary" | "success" | "error"
> = {
  queued: "default",
  running: "info",
  rendering: "secondary",
  uploading: "primary",
  done: "success",
  failed: "error",
};

export const describeOutput = (output: CreativeOutputRequest): string => {
  switch (output.type) {
    case "images":
      return `${output.count} image${output.count === 1 ? "" : "s"} · ${
        output.aspect
      }`;
    case "video":
      return `Video ${output.durationSec}s · ${output.aspect}`;
    case "klydo":
      return `Klydo ${output.durationSec}s · 1080×1080`;
    default:
      return "-";
  }
};

/**
 * Average Claude spend per job, or null. Failed jobs that spent money count too,
 * since they cost the same; jobs that failed before calling Claude do not.
 */
export const averageCostUsd = (
  jobs: CreativeJob[],
  type?: CreativeOutputType,
): number | null => {
  const costs = jobs
    .filter(
      (job) =>
        typeof job.costUsd === "number" &&
        (job.status === "done" ||
          (job.status === "failed" && job.costUsd > 0)) &&
        (!type || job.request?.output?.type === type),
    )
    .map((job) => job.costUsd as number);
  if (!costs.length) return null;
  return costs.reduce((sum, cost) => sum + cost, 0) / costs.length;
};

export const formatUsd = (value: number) => `$${value.toFixed(2)}`;
